/**
 * Deals inbox: brand requests and everything after, in four tabs by what the
 * creator has to do — new requests first, because an unanswered request is
 * a brand that books someone else.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { arr, bucketOf, needsYou, type DealBucket, type DealSummary } from '../lib/creator';
import { EmptyState, ErrorNote, PageHero, Spinner } from '../components/Primitives';
import { DealRow } from './CreatorHome';

export function CreatorDealsPage() {
  const T = useT();
  const [params, setParams] = useSearchParams();
  const [deals, setDeals] = useState<DealSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ deals?: DealSummary[] }>('/v1/partner/creator/deals');
      setDeals(arr(r.deals));
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!deals && error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!deals) return <Spinner />;

  const by = (b: DealBucket) => deals.filter((d) => bucketOf(d.status) === b);
  const counts = {
    new: by('new').length,
    active: by('active').length,
    done: by('done').length,
    closed: by('closed').length,
  };
  const asked = params.get('tab') as DealBucket | null;
  // Land where the work is: new requests if any, else what's in progress.
  const tab: DealBucket =
    asked && asked in counts
      ? asked
      : counts.new > 0
        ? 'new'
        : counts.active > 0
          ? 'active'
          : 'new';
  const list = by(tab).sort((a, b) => {
    const n = Number(needsYou(b.status)) - Number(needsYou(a.status));
    return n !== 0 ? n : (b.updatedAt ?? b.createdAt).localeCompare(a.updatedAt ?? a.createdAt);
  });
  const waiting = deals.filter((d) => needsYou(d.status)).length;

  const tabs: Array<[DealBucket, string]> = [
    ['new', T('नई', 'New')],
    ['active', T('चल रही', 'In progress')],
    ['done', T('पूरी', 'Done')],
    ['closed', T('बंद', 'Closed')],
  ];

  const empty: Record<DealBucket, { title: string; body: string }> = {
    new: {
      title: T('अभी कोई नई रिक्वेस्ट नहीं', 'No new requests'),
      body: T(
        'अपना लिंक शेयर करें और प्रोफ़ाइल पब्लिक रखें — ब्रांड की रिक्वेस्ट यहीं आएगी।',
        'Share your link and keep your profile public — brand requests land here.',
      ),
    },
    active: {
      title: T('कोई चालू डील नहीं', 'Nothing in progress'),
      body: T('मंज़ूर की गई डील यहाँ दिखेंगी।', 'Deals you accept show up here.'),
    },
    done: {
      title: T('अभी तक कोई पूरी डील नहीं', 'No completed deals yet'),
      body: T('पूरी डील और उनकी कमाई यहाँ।', 'Finished deals and what they paid.'),
    },
    closed: {
      title: T('कुछ नहीं', 'Nothing here'),
      body: T('मना की गई या रद्द डील यहाँ।', 'Declined and cancelled deals go here.'),
    },
  };

  return (
    <div className="space-y-4">
      <PageHero
        icon="jobs"
        eyebrow={T('डील', 'Deals')}
        title={
          waiting > 0
            ? T(`${waiting} आपका इंतज़ार कर रही हैं`, `${waiting} waiting on you`)
            : T('आपकी डील', 'Your deals')
        }
        sub={T(
          'ब्रांड पहले पेमेंट करता है, फिर काम शुरू।',
          'The brand pays first, then you start.',
        )}
        stats={[
          { label: T('नई', 'New'), value: String(counts.new) },
          { label: T('चल रही', 'Active'), value: String(counts.active) },
          { label: T('पूरी', 'Done'), value: String(counts.done) },
        ]}
      />

      {error && <ErrorNote message={error} onRetry={() => void load()} />}

      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setParams(k === 'new' ? {} : { tab: k }, { replace: true })}
            aria-pressed={tab === k}
            className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-semibold transition ${
              tab === k ? 'text-white shadow-lift' : 'bg-panel text-muted shadow-card'
            }`}
            style={
              tab === k
                ? { backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }
                : undefined
            }
          >
            {label}
            {counts[k] > 0 && (
              <span
                className={`tnum rounded-full px-1.5 text-[11px] ${tab === k ? 'bg-white/25' : 'bg-panel-2'}`}
              >
                {counts[k]}
              </span>
            )}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <EmptyState icon="jobs" title={empty[tab].title} body={empty[tab].body} />
      ) : (
        <ul className="space-y-2.5">
          {list.map((d) => (
            <li key={d.id}>
              <DealRow deal={d} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
